<template>
    <section class="flex flex-col gap-4" data-test="rnd-history">
        <h2 class="m-0 text-xl font-extrabold">History</h2>

        <vx-data-table
            :columns="kioskRndHistoryColumns"
            :rows="rows"
            rowKey="id"
            :loading="loading"
            :hasMore="hasMore"
            :loadingMore="loadingMore"
            :filterFields="filterFields"
            :filters="filters"
            @update:filters="updateFilters"
            @loadMore="loadMore"
        >
            <template v-slot:cell-status="{ row }">
                <kiosk-rnd-run-status :run="row" />
            </template>

            <template v-slot:cell-team_id="{ row }">
                <kiosk-team-cell :teamId="row.team_id" :teamName="row.team_name" :teamStatus="row.team_status" />
            </template>

            <template v-slot:cell-company_id="{ row }">
                <span class="font-mono">{{ row.company_id }}</span>
            </template>

            <template v-slot:cell-user_id="{ row }">{{ runByLabel(row) }}</template>

            <template v-slot:cell-trigger="{ row }">
                <span class="flex min-w-0 items-center gap-1 truncate">
                    <span data-test="run-type">{{ runTypeLabel(row) }}</span>
                    <template v-if="row.source_run_id !== null">
                        of
                        <button
                            type="button"
                            data-test="source-run"
                            :class="linkClasses"
                            @click="detailRunId = row.source_run_id"
                        >
                            #{{ row.source_run_id }}
                        </button>
                    </template>
                    <template v-if="row.followed_up_by_run_id !== null">
                        · followed up by
                        <button
                            type="button"
                            data-test="followed-up-by-run"
                            :class="linkClasses"
                            @click="detailRunId = row.followed_up_by_run_id"
                        >
                            #{{ row.followed_up_by_run_id }}
                        </button>
                    </template>
                </span>
            </template>

            <template v-slot:cell-queried="{ row }">{{ countLabel(row.queried) }}</template>

            <template v-slot:cell-reassigned="{ row }">
                <kiosk-rnd-run-breakdown :total="row.reassigned" :rows="row.reassigned_rows" label="reassigned" />
            </template>

            <template v-slot:cell-no_data="{ row }">{{ countLabel(row.no_data) }}</template>

            <template v-slot:cell-not_checked="{ row }">
                <kiosk-rnd-run-breakdown :total="row.not_checked" :rows="row.not_checked_rows" label="not checked" />
            </template>

            <template v-slot:cell-snapshot_at="{ row }">{{ runDateLabel(row.snapshot_at) }}</template>

            <template v-slot:cell-finished_at="{ row }">{{ runDateLabel(row.finished_at) }}</template>

            <template v-slot:actions="{ row }">
                <kiosk-rnd-run-row-actions :run="row" @changed="emit('changed')" @view="detailRunId = $event" />
            </template>

            <template v-slot:empty>
                <div v-if="narrowed" class="flex flex-col items-center gap-6 text-xl text-slate-500">
                    Nothing matches these filters
                    <vx-button data-test="clear-filters" color="muted-dark" @click="clearFilters"
                        >Clear filters

                        <font-awesome-icon widthAuto :icon="faCircleX" />
                    </vx-button>
                </div>
                <div v-else class="flex flex-col items-center gap-6 text-xl text-slate-500">No runs yet</div>
            </template>
        </vx-data-table>

        <kiosk-rnd-run-modal v-model:runId="detailRunId" @changed="emit('changed')" />
    </section>
</template>

<script lang="ts" setup>
import { faCircleX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxDataTable, type VxDataTableFilterSelectable } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';
import {
    countLabel,
    runByLabel,
    runDateLabel,
    runTypeLabel,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import KioskRndRunBreakdown from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunBreakdown.vue';
import KioskRndRunModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunModal.vue';
import KioskRndRunRowActions from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunRowActions.vue';
import KioskRndRunStatus from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunStatus.vue';
import {
    RND_RUNS_MAX_FILTER_VALUES,
    exceedsFilterValueLimit,
    isNarrowed,
    kioskRndHistoryColumns,
    kioskRndHistoryFilterFields,
    nextCursorFrom,
    toHistoryRow,
    toRunsParams,
    type KioskRndHistoryFilter,
} from '~/components/kiosk/kiosk-reassigned-numbers/runs/kioskRndHistoryHelpers';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient, adminTeamsClient } from '~/services';
import type { RndRun } from '~/services/adminRndClient.types';
import { isEqual } from '~/utils/helpers';

interface TeamOption {
    value: string;
    label: string;
}

const props = defineProps<{ refreshKey: number }>();
const emit = defineEmits<{ changed: [] }>();

const toasted = useToasted();

const runsCancelKey = Symbol('kiosk-rnd-runs');
const linkClasses =
    'cursor-pointer border-0 bg-transparent p-0 font-bold text-sky-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500';

const runs = ref<RndRun[]>([]);
const nextCursor = ref<string | null>(null);
const loading = ref(true);
const loadingMore = ref(false);
const filters = ref<KioskRndHistoryFilter[]>([]);
const detailRunId = ref<number | null>(null);
const knownTeams = ref(new Map<string, TeamOption>());

const {
    loading: teamsLoading,
    response: teamSearchResults,
    error: teamSearchError,
    createSearch: createTeamSearch,
    cancelSearch: cancelTeamSearch,
    cancelKey: teamsCancelKey,
} = useSearchRequest<TeamOption[]>(adminTeamsClient, 'kiosk-rnd-history-teams');

const searchTeams = createTeamSearch(async (query: string) => {
    const { data } = await adminTeamsClient.getTeams({ query }, { cancelKey: teamsCancelKey });
    const options = data.data.map((team) => ({ value: String(team.id), label: `${team.id} - ${team.name}`.trim() }));

    options.forEach((option) => knownTeams.value.set(option.value, option));

    return options;
});

const selectedTeamIds = computed(() => filters.value.find((filter) => filter.key === 'team_id')?.values ?? []);

const teamOptions = computed(() => {
    const selectedIds = new Set(selectedTeamIds.value);
    const selectedOptions = selectedTeamIds.value.flatMap((id) => knownTeams.value.get(id) ?? []);
    const unselectedResults = (teamSearchResults.value ?? []).filter((option) => !selectedIds.has(option.value));

    return [...selectedOptions, ...unselectedResults];
});

const teamSelectable = computed<VxDataTableFilterSelectable>(() => ({
    options: teamOptions.value,
    label: 'label',
    reduce: (option: TeamOption) => option.value,
    excludeSelected: true,
    loading: teamsLoading.value,
    placeholder: 'Search teams by ID or name',
    onSearch: (query: string) => searchTeams(query),
}));

const filterFields = computed(() => kioskRndHistoryFilterFields(teamSelectable.value));
const listParams = computed(() => toRunsParams(filters.value));
const narrowed = computed(() => isNarrowed(listParams.value));
const rows = computed(() => runs.value.map(toHistoryRow));
const hasMore = computed(() => nextCursor.value !== null);

const loadRuns = async () => {
    loading.value = true;
    loadingMore.value = false;
    nextCursor.value = null;

    try {
        const { data } = await adminRndClient.getRuns(listParams.value, { cancelKey: runsCancelKey });

        runs.value = data.data;
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        runs.value = [];
        loading.value = false;
        toasted.global.platform_error();
    }
};

const loadMore = async () => {
    loadingMore.value = true;

    try {
        const { data } = await adminRndClient.getRuns(
            { ...listParams.value, cursor: nextCursor.value },
            { cancelKey: runsCancelKey }
        );

        runs.value = [...runs.value, ...data.data];
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loadingMore.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loadingMore.value = false;
        toasted.global.platform_error();
    }
};

const updateFilters = (next: KioskRndHistoryFilter[]) => {
    if (exceedsFilterValueLimit(next)) {
        toasted.error(`Select up to ${RND_RUNS_MAX_FILTER_VALUES} values per filter.`);
        return;
    }

    filters.value = next;
};

const clearFilters = () => {
    filters.value = [];
};

watch(listParams, (next, previous) => {
    if (!isEqual(next, previous)) {
        loadRuns();
    }
});

watch(
    () => props.refreshKey,
    () => {
        loadRuns();
    }
);

watch(teamSearchError, (message) => {
    if (message !== undefined) {
        toasted.error(message);
    }
});

onMounted(() => {
    loadRuns();
});

onBeforeUnmount(() => {
    cancelTeamSearch();
});
</script>
